import { SubprojectConfigurationCustomNames } from '@shared/models/project-setup';
import {
  Component,
  ComponentRef,
  OnInit,
  SimpleChange,
  SimpleChanges,
  inject,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  pairwise,
  shareReplay,
  startWith,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

import { IIdentity, GridApi } from '@grid/models';
import { DataRefreshService, UserSessionChangeService } from '@shared/services';
import { AppFacade, OfflineFacade } from '@shared/state/facades';
import { SelectedSubproject } from '@shared/models';
import { DprDetails, DprDetailsSync } from '@shared/models/dpr';
import { GridPageBaseComponent } from '@shared/models/component/grid-page-base.component';
import { RefreshType, Rights } from '@shared/enums';
import { AutoSaveableComponent } from '@shared/models/component';
import {
  EditableComponent,
  isEditableComponent,
} from './editable-component.model';
import {
  isOnlineDependentComponent,
  OnlineDependentComponent,
} from './online-dependent-component.model';
import { SaveErrorHandlerService } from '@dpr-details/services';

@UntilDestroy()
@Component({
  template: '',
})
export abstract class DprDetailsGridContainerBaseComponent<T extends IIdentity>
  extends GridPageBaseComponent<T>
  implements OnInit, AutoSaveableComponent
{
  isAppOnline!: boolean;
  isAppOnline$ = new BehaviorSubject<boolean>(false);
  canEdit!: boolean;
  canEdit$ = new BehaviorSubject<boolean>(false);

  dprDetails!: DprDetails;
  selectedClientId: string;
  selectedSubproject: SelectedSubproject;
  dprDetailsRouteParams: Observable<Params>;
  customTabNames: SubprojectConfigurationCustomNames;

  dprDetails$!: Observable<DprDetails>;
  dprDetailsOfflineChanges!: DprDetailsSync;
  dprDetailsOfflineChanges$!: Observable<DprDetailsSync>;
  hasChanges$ = new BehaviorSubject<boolean>(false);
  isDirty$ = new BehaviorSubject<boolean>(false);

  selectedSubproject$: Observable<SelectedSubproject>;

  activatedComponent: ComponentRef<any>;

  protected offlineFacade = inject(OfflineFacade);
  protected appFacade = inject(AppFacade);
  protected dataRefreshService = inject(DataRefreshService);
  protected userSessionChangeService = inject(UserSessionChangeService);
  protected saveErrorHandlerService = inject(SaveErrorHandlerService);

  private canEditSub!: Subscription;
  private isAppOnlineSub!: Subscription;

  constructor(protected activatedRoute: ActivatedRoute) {
    super();

    this.unsavedChangesService.register(this.save.bind(this), this.isDirty$);
  }

  //#region Lifecycle hooks

  ngOnInit(): void {
    this.initializeSelectedSubproject();
    this.initializeDprDetails();
    this.initializeDprDetailsOfflineChanges();

    this.dataRefreshService
      .getRefreshNeeded()
      .pipe(
        untilDestroyed(this),
        filter((refreshType) => refreshType === RefreshType.Data),
        tap(() => {
          this.refreshData();
        })
      )
      .subscribe();

    combineLatest([this.selectedSubproject$, this.dprDetails$])
      .pipe(
        untilDestroyed(this),
        tap(([selectedSubproject, dprDetails]) => {
          this.canEdit =
            selectedSubproject.dprScreen === Rights.Edit &&
            !selectedSubproject.isLocked &&
            !dprDetails.lockedDate;
          this.canEdit$.next(this.canEdit);
        })
      )
      .subscribe();

    this.appFacade.query.isAppOnline$
      .pipe(untilDestroyed(this))
      .subscribe((isAppOnline) => {
        this.isAppOnline = isAppOnline;
        this.isAppOnline$.next(this.isAppOnline);
      });

    this.userDetailsFacade.query.selectedClientId$
      .pipe(untilDestroyed(this))
      .subscribe((clientId) => {
        this.selectedClientId = clientId;
      });
  }

  //#endregion

  //#region Deactivation guard

  canDeactivate(): boolean | Observable<boolean> {
    return !this.isDirty$.value;
  }

  //#endregion

  //#region AutoSaveableComponent

  autoSave(): void {
    if (this.canEdit && this.isDirty$.value) {
      this.save(null, true);
    }
  }

  autoSaveFromAdminChange(): Observable<boolean> {
    if (this.canEdit && this.isDirty$.value) {
      return this.save(null, false, true);
    }

    return of(false);
  }

  //#endregion

  onGridRegistered(gridApi: GridApi<T>): void {
    this.gridApi = gridApi;

    gridApi.isDirty$
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.isDirty$.next(true);
        })
      )
      .subscribe();
  }

  protected setupIsAppOnlineBinding(): void {
    this.isAppOnlineSub?.unsubscribe();
    this.isAppOnlineSub = this.isAppOnline$
      .pipe(
        untilDestroyed(this),
        filter(
          () =>
            this.activatedComponent &&
            isOnlineDependentComponent(this.activatedComponent)
        )
      )
      .subscribe((isAppOnline) => {
        const activatedComponent = this
          .activatedComponent as unknown as OnlineDependentComponent;

        activatedComponent.isAppOnline = isAppOnline;

        const changes: SimpleChanges = {
          isAppOnline: new SimpleChange(null, this.isAppOnline, false),
        };

        activatedComponent.ngOnChanges(changes);
      });
  }

  protected setupCanEditBinding(): void {
    this.canEditSub?.unsubscribe();
    this.canEditSub = this.canEdit$
      .pipe(
        untilDestroyed(this),
        filter(
          () =>
            this.activatedComponent &&
            isEditableComponent(!this.activatedComponent)
        )
      )
      .subscribe((canEdit) => {
        const activatedComponent = this
          .activatedComponent as unknown as EditableComponent;

        activatedComponent.canEdit = canEdit;

        const changes: SimpleChanges = {
          canEdit: new SimpleChange(null, this.canEdit, true),
        };

        (activatedComponent as any).ngOnChanges(changes);
      });
  }

  protected resetGrids(): void {
    this.gridApi?.reset();
  }

  //#region Private methods

  private initializeSelectedSubproject(): void {
    // verify if user rights changed: if yes, do not emit a value,
    // to avoid triggering a data refresh, that could result in data loss if there are unsaved changes
    this.selectedSubproject$ =
      this.userDetailsFacade.query.selectedSubproject$.pipe(
        startWith(null),
        pairwise(),
        filter(
          ([previousSelectedSubproject, currentSelectedSubproject]) =>
            currentSelectedSubproject?.data != null &&
            !this.userSessionChangeService.isUserSessionChange(
              previousSelectedSubproject,
              currentSelectedSubproject
            )
        ),
        map(([_, currentSelectedSubproject]) => currentSelectedSubproject),
        tap((selectedSubproject) => {
          this.selectedSubproject = selectedSubproject;
          this.customTabNames =
            this.selectedSubproject.data.subprojectConfiguration.customNames;
        }),
        shareReplay()
      );
  }

  private initializeDprDetails(): void {
    const routeParams =
      this.dprDetailsRouteParams ?? this.activatedRoute.parent.params;

    this.dprDetails$ = routeParams.pipe(
      map((params) => params.dprDetailId),
      distinctUntilChanged(),
      switchMap((dprDetailId) => {
        return this.offlineFacade.getDprDetails(dprDetailId);
      }),
      filter((dprDetails) => !!dprDetails),
      tap((dprDetails) => {
        this.dprDetails = dprDetails;
        this.resetGrids();
        this.isDirty$.next(false);
      }),
      shareReplay()
    );
  }

  private initializeDprDetailsOfflineChanges(): void {
    const routeParams =
      this.dprDetailsRouteParams ?? this.activatedRoute.parent.params;

    this.dprDetailsOfflineChanges$ = combineLatest([
      routeParams,
      this.offlineFacade.query.dprDetailsOfflineChanges$,
    ]).pipe(
      switchMap(([params, dprDetailsOfflineChanges]) =>
        of(
          // double equal needed, because in offline the ids are sent as strings
          // eslint-disable-next-line eqeqeq
          dprDetailsOfflineChanges?.find((x) => x.id == params.dprDetailId)
        )
      ),
      tap((dprDetailsOfflineChanges) => {
        this.dprDetailsOfflineChanges = dprDetailsOfflineChanges;
      }),
      shareReplay()
    );
  }

  //#endregion

  protected abstract refreshData(): void;
}
